import { STORAGE_LANGUAGE_NAME } from "constants/variables";
import { getLocalStorageItem, setLocalStorageItem } from "utils/localStorage";
import { useLocalizationStore } from "stores/localization";
import { isAvailableLang } from "./is";

const RESERVED_PATH_SEGMENTS = new Set([
  "pages",
  "sportsbook",
  "live",
  "casino",
  "games",
  "profile",
  "bonus",
]);

/** Валидный ISO: 2–3 латинские буквы */
export const isIsoLang = (v: string | null | undefined): v is string =>
  !!v && /^[a-z]{2,3}$/i.test(v);

/**
 * Инициализирует язык с приоритетом:
 * 1) ?lang= (EN/ru/kk и т.п.) – регистронезависимо
 * 2) первый сегмент pathname, если это ISO (и не зарезервирован)
 * 3) localStorage
 * 4) 'en'
 *
 * В localStorage пишем только валидный ISO.
 */
export const getInitLanguage = (): string => {
  const { search, pathname } = window.location;

  const qs = new URLSearchParams(search);
  // 1) query-параметр
  const q = (qs.get(STORAGE_LANGUAGE_NAME) || "").trim().toLowerCase();

  if (isIsoLang(q)) {
    setLocalStorageItem(STORAGE_LANGUAGE_NAME, q);
    return q;
  }

  // 2) язык из пути
  const firstSeg = pathname.split("/").filter(Boolean)[0]?.trim().toLowerCase();

  if (
    isIsoLang(firstSeg) &&
    isAvailableLang(firstSeg) &&
    !RESERVED_PATH_SEGMENTS.has(firstSeg) // не "pages" и т.п.
  ) {
    setLocalStorageItem(STORAGE_LANGUAGE_NAME, firstSeg);
    return firstSeg;
  }

  // 3) localStorage
  const stored = (getLocalStorageItem(STORAGE_LANGUAGE_NAME) || "")
    .trim()
    .toLowerCase();
  if (isIsoLang(stored)) {
    return stored;
  }

  // 4) дефолт
  setLocalStorageItem(STORAGE_LANGUAGE_NAME, "en");
  return "en";
};

/**
 * Применяем язык из профиля ПОСЛЕ логина, если в URL нет ?lang=
 * (приоритет параметра сохраняем).
 */
export const applyLoginLanguage = (profileIso?: string | null) => {
  try {
    const iso = (profileIso || "").trim().toLowerCase();
    if (!isIsoLang(iso)) {
      return;
    }

    // сверяем со списком языков из стора (если уже загружен)
    const valid = useLocalizationStore
      .getState()
      .languages.map((l) => l.isoCode.toLowerCase());
    if (valid.length && !valid.includes(iso)) {
      return;
    }

    const { language, changeLanguage } = useLocalizationStore.getState();
    if ((language || "").toLowerCase() !== iso) {
      changeLanguage(iso);
    }
  } catch {
    /* error */
  }
};
